<template>
  <l-modal :title="t('bill-discount.title-plural')" :button="false" @close="close">
    <l-option-selector v-model="selectedOption" :options="discountTypes" />

    <div v-if="selectedOption === 'promotion'" class="mt-4">
      <div v-if="promotionsWithoutPoints.length > 0">
        <div class="flex heading-3">
          {{ t('bill-discount.no-points-discounts') }}
        </div>
        <div class="grid grid-cols-3 gap-3 my-6">
          <promotion-item
            v-for="promotion in promotionsWithoutPoints"
            :key="promotion.id"
            :promotion="promotion"
            :selected="editingPromotion.promotionId === promotion.id"
            @click="promotionSelected(promotion)"
          />
        </div>
      </div>
      <div v-if="promotionsWithPoints.length > 0">
        <div class="flex flex-col">
          <div class="heading-3">
            {{ t('bill-discount.points-discounts') }}
          </div>
          <div class="font-body">
            {{ t('bill-discount.available-points') }}:
            {{ customerPoints }}
          </div>
        </div>
        <div class="flex flex-wrap gap-3 my-6">
          <promotion-item
            v-for="promotion in promotionsWithPoints"
            :key="promotion.id"
            :promotion="promotion"
            :selected="editingPromotion.promotionId === promotion.id"
            @click="promotionSelected(promotion)"
          />
        </div>
      </div>
    </div>

    <div v-else class="mt-4 flex flex-col gap-4">
      <l-field :label="t('bill-discount.discount-to-apply')">
        <l-number-input
          v-if="editingPromotion.discountType === 'currency'"
          v-model="editingPromotion.discountAmount"
          :min="0"
          :icon="currencyIcon"
          format="currency"
        />
        <l-number-input
          v-else
          v-model="editingPromotion.discountAmount"
          :min="0"
          :max="100"
          icon="percentage"
          format="decimal"
        />
      </l-field>
      <l-field :label="t('bill-discount.concept')">
        <l-textarea v-model="editingPromotion.name" />
      </l-field>
    </div>

    <template #footer>
      <div class="flex gap-2 w-full">
        <l-button v-if="props.discount" type="secondary" class="flex-1" @click="remove">
          {{ t('bill-discount.remove-promotion') }}
        </l-button>
        <l-button class="flex-1" :disabled="wrongAmount" @click="save">
          {{
            props.discount ? t('bill-discount.edit-promotion') : t('bill-discount.add-promotion')
          }}
        </l-button>
      </div>
    </template>
  </l-modal>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { PromotionFilter } from '@last/core'
import {
  LButton,
  LField,
  LModal,
  LNumberInput,
  LOptionSelector,
  LTextarea
} from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/LOptionSelector.vue'

import { getCustomerPromotions } from '@/api/customers'
import { useTabs } from '@/composables/useTabs'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { usePromotionsStore } from '@/store/promotions'
import type { NewTabPromotion, Promotion } from '@/types'

import PromotionItem from './PromotionItem.vue'

interface Props {
  tabId: string
  originalTotal: number
  discount?: NewTabPromotion | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  deletePromotion: []
  updatePromotion: [promotion: NewTabPromotion]
}>()

const { t } = useI18n()

const authStore = useAuthStore()
const promotionsStore = usePromotionsStore()
const configStore = useConfigStore()

const { locationId } = storeToRefs(authStore)
const {
  promotionsWithPoints: getPromotionsWithPoints,
  promotionsWithoutPoints: getPromotionsWithoutPoints
} = storeToRefs(promotionsStore)
const { currencyIcon } = storeToRefs(configStore)

const { tab, customerId, total } = useTabs(() => props.tabId)

const customerPoints = ref(0)
const editingPromotion = ref<NewTabPromotion>({
  discountType: 'percentage',
  discountAmount: 0,
  global: true
})
const customerRestrictedPromotions = ref<Promotion[]>([])
const customerUsedPromotions = ref<string[]>([])
const selectedOption = ref<'percentage' | 'currency' | 'promotion'>('percentage')

const wrongAmount = computed(() => {
  if (editingPromotion.value.discountType === 'currency') {
    if ((editingPromotion.value.discountAmount ?? 0) > props.originalTotal) {
      return true
    }
  } else if (editingPromotion.value.discountType === 'percentage') {
    const amount = parseInt(String(editingPromotion.value.discountAmount))
    if (amount < 0 || amount > 100 || isNaN(amount)) return true
  }
  return false
})

const promotionsWithPoints = computed(() => {
  return getPromotionsWithPoints.value
    .concat(customerRestrictedPromotions.value)
    .filter(promotion => {
      return (
        checkIfPromotionCanBeShown(promotion) &&
        promotion.pointsExpense &&
        promotion.pointsExpense <= customerPoints.value
      )
    }) as (Promotion & { discountType: 'currency' | 'percentage' })[]
})

const promotionsWithoutPoints = computed(() => {
  return getPromotionsWithoutPoints.value
    .concat(customerRestrictedPromotions.value)
    .filter(promotion => {
      return checkIfPromotionCanBeShown(promotion)
    }) as (Promotion & { discountType: 'currency' | 'percentage' })[]
})

const discountTypes = computed(() => {
  const types: Option[] = [
    {
      icon: 'cash',
      value: 'currency',
      label: t('product-discount.cash')
    },
    {
      icon: 'percentage',
      value: 'percentage',
      label: t('product-discount.percentage')
    }
  ]
  if (promotionsWithPoints.value.length > 0 || promotionsWithoutPoints.value.length > 0) {
    types.push({
      icon: 'discount',
      value: 'promotion',
      label: t('bill-discount.promotion')
    })
  }
  return types
})

function close() {
  emit('close')
}

function promotionSelected(promotion: Promotion & { discountType: 'currency' | 'percentage' }) {
  if (editingPromotion.value.promotionId === promotion.id) {
    editingPromotion.value = {
      discountType: 'percentage',
      discountAmount: 0,
      global: true
    }
  } else {
    editingPromotion.value = {
      discountType: promotion.discountType,
      discountAmount: promotion.discountAmount!,
      promotionId: promotion.id,
      pointsExpense: promotion.pointsExpense ?? undefined,
      name: promotion.name,
      freeDelivery: promotion.freeDelivery,
      global: true
    }
  }
}

function remove() {
  emit('deletePromotion')
  emit('close')
}

function save() {
  if (!editingPromotion.value.discountAmount) {
    editingPromotion.value.discountAmount = 0
  }
  emit('updatePromotion', { ...editingPromotion.value, global: true })
  emit('close')
}

function checkIfPromotionCanBeShown(promotion: Promotion) {
  const isNotProductOrCategory = !promotion.products && !promotion.categories

  let isCorrectAmount = true
  if (promotion.discountType === 'currency') {
    isCorrectAmount = promotion.discountAmount! <= props.originalTotal
  }
  const isCorrectType = ['currency', 'percentage'].includes(promotion.discountType)

  const today = format(new Date(), 'eeee', { locale: enUS }).toLowerCase()

  return (
    isNotProductOrCategory &&
    isCorrectType &&
    isCorrectAmount &&
    new PromotionFilter(
      total.value!,
      locationId.value,
      today,
      tab.value!.pickupType,
      customerId.value,
      customerUsedPromotions.value
    ).isPromotionValid(promotion)
  )
}

onMounted(async () => {
  if (props.discount) {
    if (props.discount.promotionId) {
      selectedOption.value = 'promotion'
    } else if (
      props.discount.discountType === 'currency' ||
      props.discount.discountType === 'percentage'
    ) {
      selectedOption.value = props.discount.discountType
    }
    editingPromotion.value = { ...props.discount }
  }
  if (customerId.value) {
    const response = await getCustomerPromotions(customerId.value)
    customerPoints.value = response.points
    customerRestrictedPromotions.value = response.customerPromotions
    customerUsedPromotions.value = response.customerUsedPromotions
  }
})

watch(
  selectedOption,
  () => {
    if (selectedOption.value !== 'promotion') {
      editingPromotion.value = {
        discountType: selectedOption.value,
        discountAmount: 0,
        global: true
      }
    }
  },
  { immediate: true }
)
</script>
